/**
 * Info File Generator for Data Upload
 * Generates and downloads info text file with upload metadata
 */

import { InfoFileParams } from '../types/dataUpload.types';

/**
 * Generates info file content with upload metadata
 */
export const generateInfoFileContent = (params: InfoFileParams): string => {
  const {
    originalFileNames,
    delimiter,
    radioHasHeader,
    selectedTimezone,
    hasSeparateDateAndTime,
    selectedColumn1,
    selectedColumn2,
    selectedColumn3,
    valueColumnName,
    customDateFormat,
    t
  } = params;

  const now = new Date();
  const timestamp = now.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  let content = `${t('infoFile.header')}\n`;
  content += `${'='.repeat(60)}\n\n`;

  // Generation timestamp
  content += `${t('infoFile.generatedAt')}: ${timestamp}\n\n`;

  // Uploaded files
  content += `${t('infoFile.uploadedFiles')}:\n`;
  content += `${'-'.repeat(40)}\n`;
  originalFileNames.forEach((fileName, index) => {
    content += `${index + 1}. ${fileName}\n`;
  });
  content += `\n`;

  // Processing parameters
  content += `${t('infoFile.parameters')}:\n`;
  content += `${'-'.repeat(40)}\n`;
  
  // Delimiter name — falls back to raw char only for truly unknown delimiters
  const delimiterName = t(`delimiterNames.${delimiter}`, { defaultValue: delimiter });
  content += `${t('infoFile.delimiter')}: ${delimiterName}\n`;
  
  content += `${t('infoFile.hasHeaders')}: ${radioHasHeader === 'ja' ? t('common.yes', { ns: 'common' }) : t('common.no', { ns: 'common' })}\n`;
  content += `${t('infoFile.timezone')}: ${selectedTimezone}\n`;
  content += `${t('infoFile.dateTimeMode')}: ${hasSeparateDateAndTime ? t('infoFile.separateDateTime') : t('infoFile.combinedDateTime')}\n`;
  content += `\n`;

  // Column mappings
  content += `${t('infoFile.columnMappings')}:\n`;
  content += `${'-'.repeat(40)}\n`;
  content += `${t('infoFile.dateColumn')}: ${selectedColumn1 || '-'}\n`;
  
  if (hasSeparateDateAndTime) {
    content += `${t('infoFile.timeColumn')}: ${selectedColumn2 || '-'}\n`;
    content += `${t('infoFile.valueColumn')}: ${selectedColumn3 || '-'}\n`;
  } else {
    content += `${t('infoFile.valueColumn')}: ${selectedColumn2 || '-'}\n`;
  }

  if (valueColumnName) {
    content += `${t('infoFile.valueColumnName')}: ${valueColumnName}\n`;
  }

  if (customDateFormat) {
    content += `${t('infoFile.customDateFormat')}: ${customDateFormat}\n`;
  }

  content += `\n${'='.repeat(60)}\n`;

  return content;
};

/**
 * Downloads info file with the given content
 */
export const downloadInfoFile = (baseFileName: string, content: string): void => {
  try {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${baseFileName}_info.txt`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading info file:', error);
    // Don't show error to user as this is a supplementary file
  }
};
